import { ApiError } from "./api";

/**
 * Friendly copy for the backend's documented error codes on the template
 * WRITE endpoints — POST /api/portal/templates (create) and
 * PUT /api/portal/templates/{templateId} (rename / re-save the design).
 *
 * round8/template-authoring. This lives in its own module rather than inside
 * a page because it now has two consumers — TemplateEditorPage (create and
 * full save) and SettingsPage's inline rename — and exporting a plain
 * function from a component file breaks react-refresh
 * (react-refresh/only-export-components, which is enforced here).
 *
 * Most of these codes are not reachable through ordinary interaction: the
 * name inputs carry maxLength={100} and both call sites reject an
 * empty/whitespace name before writing (so INVALID_NAME needs a direct API
 * call or a rejection mock), and both refuse to submit a null snapshot (so
 * MISSING_DESIGN_STATE / INVALID_DESIGN_STATE likewise). They are mapped
 * anyway because the client-side checks are a courtesy, never the
 * enforcement — the server is the trust boundary, and if the two ever drift
 * the builder should still get a sentence they can act on.
 *
 * Anything not listed falls through to the server's own message, per
 * components/friendlyErrorMessage.ts's documented convention: callers with a
 * known-code mapping check `err.code` themselves first and only fall back
 * for the unmapped case (INVALID_JSON / INTERNAL_ERROR / TENANT_NOT_FOUND —
 * generic failures with no better client-side framing).
 */
export function templateWriteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "FORBIDDEN":
        return "Only the account owner can save templates.";
      case "TEMPLATE_LIMIT_REACHED":
        return "You've reached the limit of 20 saved templates — delete one below first.";
      case "INVALID_NAME":
        return "Enter a template name (1-100 characters).";
      case "MISSING_DESIGN_STATE":
      case "INVALID_DESIGN_STATE":
        return "This design can't be saved as a template — it has no usable geometry.";
      case "NOT_FOUND":
        return "That template no longer exists — it may have been deleted.";
      case "NO_FIELDS_TO_UPDATE":
        return "Nothing to save — change the name or the design first.";
    }
  }
  return err instanceof Error ? err.message : "Failed to save template";
}
